import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Globe } from "lucide-react";
import Logo from "./Logo";
import { COMPANY, SERVICES } from "../data/mock";
import { useToast } from "../hooks/use-toast";

export default function Footer() {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({ title: "You\u2019re subscribed!", description: `We\u2019ll send buyer opportunities to ${email}.` });
    setEmail("");
  };

  return (
    <footer className="bg-navy-brand text-slate-300">
      {/* Buyers list CTA */}
      <div className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-5 py-14 text-center lg:flex-row lg:justify-between lg:text-left lg:px-8">
          <div>
            <h3 className="text-2xl font-bold text-white lg:text-3xl">Looking to Purchase Inventory?</h3>
            <p className="mt-2 text-slate-400">Subscribe to our Buyers List for new opportunities today.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md items-center gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="h-12 w-full rounded-full border border-white/15 bg-white/5 px-5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-teal-brand"
            />
            <button
              type="submit"
              className="flex h-12 shrink-0 items-center gap-1.5 rounded-full bg-teal-brand px-6 text-sm font-semibold text-white transition-transform hover:scale-105"
            >
              Subscribe <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:grid-cols-4 lg:px-8">
        <div className="lg:col-span-1">
          <div className="rounded-xl bg-white/95 p-3 w-fit">
            <Logo />
          </div>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            {COMPANY.name} has been in the liquidation business for decades. We buy any size and variety of lot — new, salvage, discontinued and insurance-recovery — managing the full product cycle while maximizing our clients’ return.
          </p>
        </div>

        <div>
          <h4 className="mb-4 font-bold text-white">Quick Links</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/" className="transition-colors hover:text-teal-brand">Homepage</Link></li>
            <li><Link to="/how-it-works" className="transition-colors hover:text-teal-brand">How it Works</Link></li>
            <li><Link to="/amazon-overstock" className="transition-colors hover:text-teal-brand">We Buy Amazon Overstock</Link></li>
            <li><Link to="/submit-inventory" className="transition-colors hover:text-teal-brand">Submit Inventory</Link></li>
            <li><Link to="/blog" className="transition-colors hover:text-teal-brand">Blog</Link></li>
            <li><Link to="/contact" className="transition-colors hover:text-teal-brand">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-bold text-white">Services</h4>
          <ul className="space-y-2.5 text-sm">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <Link to="/services" className="transition-colors hover:text-teal-brand">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-bold text-white">Get in Touch</h4>
          <ul className="space-y-3.5 text-sm">
            <li className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5 shrink-0 text-teal-brand" />
              <span>{COMPANY.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={18} className="shrink-0 text-teal-brand" />
              <a href={COMPANY.phoneHref} className="transition-colors hover:text-teal-brand">{COMPANY.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={18} className="shrink-0 text-teal-brand" />
              <a href={`mailto:${COMPANY.email}`} className="transition-colors hover:text-teal-brand">{COMPANY.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Globe size={18} className="shrink-0 text-teal-brand" />
              <span>{COMPANY.website}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 text-xs text-slate-400 sm:flex-row lg:px-8">
          <p>Copyright © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
          <div className="flex gap-5">
            <a href="#terms" className="transition-colors hover:text-teal-brand">Terms of Service</a>
            <a href="#privacy" className="transition-colors hover:text-teal-brand">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
